<template>
  <div
    id="event-types-section"
    class="border-b border-slate-50 dark:border-slate-700 items-center flex p-4 text-black-900 dark:text-slate-300 row"
  >
    <div class="w-[25%] py-4 pr-6 ml-0">
      <h4 class="block-title text-black-900 dark:text-slate-200">
        {{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.TITLE') }}
      </h4>
      <p>{{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.NOTE') }}</p>
    </div>
    <div class="p-4 w-[45%]">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-4">
        <spinner size="small" />
        <span class="ml-2">{{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.LOADING') }}</span>
      </div>

      <!-- Event Types List -->
      <div v-else>
        <!-- Header with Sync Button -->
        <div class="flex items-center justify-between mb-4" v-if="eventTypes.length > 0">
          <span class="text-sm text-slate-600 dark:text-slate-400">
            {{ eventTypes.length }} {{ eventTypes.length === 1 ? 'event' : 'events' }} configured
          </span>
          <button
            v-tooltip="$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.SYNC_TOOLTIP')"
            class="sync-button"
            :class="{ 'is-syncing': isSyncing }"
            :disabled="isSyncing"
            @click="syncEventTypes"
          >
            <svg 
              v-if="!isSyncing"
              xmlns="http://www.w3.org/2000/svg" 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
              class="sync-icon"
            >
              <path d="M21 2v6h-6"></path>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
              <path d="M3 22v-6h6"></path>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
            </svg>
            <spinner v-else size="tiny" />
            <span>{{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.SYNC_BUTTON') }}</span>
          </button>
        </div>

        <div v-if="eventTypes.length === 0" class="empty-state mb-4">
          <p class="text-slate-500 dark:text-slate-400">
            {{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.EMPTY') }}
          </p>
        </div>

        <div v-else class="event-types-list mb-4">
          <div
            v-for="eventType in eventTypes"
            :key="eventType.id"
            class="event-type-item flex items-center justify-between p-3 mb-2 bg-slate-50 dark:bg-slate-800 rounded-lg"
          >
            <div class="event-info flex items-center gap-2">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
                class="text-slate-400"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <div>
                <span class="font-medium text-slate-800 dark:text-slate-200">
                  {{ eventType.name || 'Unnamed Event' }}
                </span>
                <span class="ml-2 text-sm text-slate-500 dark:text-slate-400">
                  ({{ eventType.duration || 15 }} min)
                </span>
              </div>
            </div>
            <woot-button
              variant="smooth"
              color-scheme="alert"
              size="tiny"
              icon="delete"
              :is-loading="deletingId === eventType.id"
              @click="deleteEventType(eventType.id)"
            />
          </div>
        </div>

        <!-- Add New Event Type Form -->
        <div class="add-event-form border-t border-slate-100 dark:border-slate-700 pt-4">
          <h5 class="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300">
            {{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.ADD_NEW') }}
          </h5>
          
          <div class="mb-3">
            <woot-input
              v-model="newEventType.event_id"
              :label="$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.EVENT_ID_LABEL')"
              :placeholder="$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.EVENT_ID_PLACEHOLDER')"
              :readonly="!!fetchedEventDetails"
              size="small"
            />
            <p class="text-xs text-slate-500 mt-1">
              {{ fetchedEventDetails ? 'Event ID verified. Click "Add Event Type" to save.' : $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.EVENT_ID_HELP') }}
            </p>
          </div>

          <!-- Fetched Event Details Preview -->
          <div v-if="fetchedEventDetails" class="fetched-details mb-3 p-3 bg-green-50 dark:bg-green-900 rounded-lg">
            <p class="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
              Event Details Found:
            </p>
            <p class="text-sm text-green-700 dark:text-green-300">
              <strong>Name:</strong> {{ fetchedEventDetails.title }}
            </p>
            <p class="text-sm text-green-700 dark:text-green-300">
              <strong>Duration:</strong> {{ fetchedEventDetails.duration }} min
            </p>
            <p class="text-sm text-green-700 dark:text-green-300 truncate" :title="fetchedEventDetails.eventTypeId">
              <strong>Event ID:</strong> {{ fetchedEventDetails.eventTypeId }}
            </p>
          </div>

          <!-- Fetch Error -->
          <div v-if="fetchError" class="fetch-error mb-3 p-3 bg-red-50 dark:bg-red-900 rounded-lg">
            <p class="text-sm text-red-700 dark:text-red-300">
              {{ fetchError }}
            </p>
          </div>

          <div class="flex gap-2">
            <!-- Show "Back" button when event details are fetched -->
            <woot-button
              v-if="fetchedEventDetails"
              size="small"
              variant="smooth"
              color-scheme="secondary"
              @click="clearFetchedDetails"
            >
              {{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.BACK_BUTTON') }}
            </woot-button>
            <!-- Show "Fetch Details" button when no event details are fetched -->
            <woot-button
              v-else
              :disabled="!newEventType.event_id || isFetching"
              size="small"
              variant="smooth"
              color-scheme="secondary"
              class="flex items-center justify-center"
              @click="fetchEventDetails"
            >
              <template v-if="!isFetching">
                {{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.FETCH_DETAILS') }}
              </template>

              <template v-else>
                <spinner size="small" />
              </template>
            </woot-button>
            <woot-button
              :is-loading="isSaving"
              :disabled="!fetchedEventDetails"
              size="small"
              @click="addEventType"
            >
              {{ $t('PROFILE_SETTINGS.FORM.EVENT_TYPES.ADD_BUTTON') }}
            </woot-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
import userEventTypesAPI from 'dashboard/api/userEventTypes';

export default {
  components: {
    Spinner,
  },
  mixins: [alertMixin],
  data() {
    return {
      eventTypes: [],
      isLoading: true,
      isSaving: false,
      isFetching: false,
      isSyncing: false,
      deletingId: null,
      newEventType: {
        event_id: '',
      },
      fetchedEventDetails: null,
      fetchError: null,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
  },
  mounted() {
    this.fetchEventTypes();
  },
  watch: {
    accountId() {
      this.isLoading = true;
      this.eventTypes = [];
      this.fetchEventTypes();
    },
    isLoading(loading) {
      if (!loading) {
        this.maybeFocusEventIdField();
      }
    },
  },
  methods: {
    async fetchEventTypes() {
      try {
        const response = await userEventTypesAPI.getAll();
        this.eventTypes = response.data;
      } catch (error) {
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.FETCH_ERROR'));
      } finally {
        this.isLoading = false;
        this.$nextTick(() => this.maybeFocusEventIdField());
      }
    },
    maybeFocusEventIdField() {
      if (this.$route.query.focus !== 'event-types') return;
      const input = this.$el?.querySelector?.('.add-event-form input[type="text"]');
      if (!input) return;
      this.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus();
      // Clear query so refresh doesn't keep skipping signature focus
      const query = { ...this.$route.query };
      delete query.focus;
      this.$router.replace({ query });
    },
    async syncEventTypes() {
      if (this.eventTypes.length === 0) {
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.SYNC_NO_EVENTS'));
        return;
      }

      this.isSyncing = true;
      try {
        const response = await userEventTypesAPI.sync();
        if (response.data?.success) {
          // Update local event types with synced data (only events that exist in calendar)
          this.eventTypes = response.data.data;
          const message = response.data.message || this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.SYNC_SUCCESS');
          this.showAlert(message);
          
          // Log deleted events if any
          if (response.data.deleted?.length > 0) {
            console.info('Events removed (not found in calendar):', response.data.deleted);
          }
        } else {
          this.showAlert(response.data?.error || this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.SYNC_ERROR'));
        }
      } catch (error) {
        console.error('Error syncing events:', error);
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.SYNC_ERROR'));
      } finally {
        this.isSyncing = false;
      }
    },
    async fetchEventDetails() {
      if (!this.newEventType.event_id) return;

      this.isFetching = true;
      this.fetchedEventDetails = null;
      this.fetchError = null;

      try {
        // Call backend API to fetch event details
        const response = await userEventTypesAPI.fetchEventDetails(
          this.newEventType.event_id
        );

        if (response.data?.success && response.data?.data) {
          this.fetchedEventDetails = response.data.data;
        } else {
          this.fetchError = response.data?.error || 'Event not found. Please check the Event ID.';
        }
      } catch (error) {
        console.error('Error fetching event details:', error);
        this.fetchError = error.response?.data?.error || 'Failed to fetch event details. Please check the Event ID.';
      } finally {
        this.isFetching = false;
      }
    },
    async addEventType() {
      if (!this.fetchedEventDetails) return;

      this.isSaving = true;
      try {
        // Use eventTypeId from the fetched response, not from the input field
        // The input field value might have been modified by the user after fetching
        const eventData = {
          event_id: this.fetchedEventDetails.eventTypeId,
          schedule_id: this.fetchedEventDetails.scheduleId,
          name: this.fetchedEventDetails.title,
          duration: this.fetchedEventDetails.duration,
        };

        const response = await userEventTypesAPI.create(eventData);
        this.eventTypes.push(response.data);
        this.resetForm();
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.ADD_SUCCESS'));
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || 
          this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.ADD_ERROR');
        this.showAlert(errorMessage);
      } finally {
        this.isSaving = false;
      }
    },
    async deleteEventType(id) {
      this.deletingId = id;
      try {
        await userEventTypesAPI.delete(id);
        this.eventTypes = this.eventTypes.filter(e => e.id !== id);
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.DELETE_SUCCESS'));
      } catch (error) {
        this.showAlert(this.$t('PROFILE_SETTINGS.FORM.EVENT_TYPES.DELETE_ERROR'));
      } finally {
        this.deletingId = null;
      }
    },
    resetForm() {
      this.newEventType = {
        event_id: '',
      };
      this.fetchedEventDetails = null;
      this.fetchError = null;
    },
    clearFetchedDetails() {
      // Only clear the fetched details, keep the event_id input
      this.fetchedEventDetails = null;
      this.fetchError = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.sync-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--s-700);
  background-color: var(--s-75);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: var(--s-100);
    border-color: var(--s-300);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.is-syncing .sync-icon {
    animation: spin 1s linear infinite;
  }

  .sync-icon {
    flex-shrink: 0;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.event-type-item {
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--s-100);
  }
}

.empty-state {
  padding: var(--space-normal);
  text-align: center;
  background-color: var(--s-50);
  border-radius: var(--border-radius-normal);
}

.fetched-details {
  border: 1px solid var(--g-300);
}

.fetch-error {
  border: 1px solid var(--r-300);
}

// Dark mode styles
:global(.dark) {
  .sync-button {
    color: var(--s-200);
    background-color: var(--s-800);
    border-color: var(--s-600);

    &:hover:not(:disabled) {
      background-color: var(--s-700);
      border-color: var(--s-500);
    }
  }
}
</style>
